import React from "react";
import {
  MessageSquare,
  ExternalLink,
  Phone,
  Copy,
  Trash2,
  Plus,
  GripVertical,
} from "lucide-react";

import { Card, Hint, TextInput } from "./ui";
import { countVariables } from "../templateUtils";

const TYPE_META = {
  QUICK_REPLY: { label: "Quick reply", icon: MessageSquare },
  URL: { label: "Visit website", icon: ExternalLink },
  PHONE_NUMBER: { label: "Call phone number", icon: Phone },
  COPY_CODE: { label: "Copy offer code", icon: Copy },
};

const LIMITS = { URL: 2, PHONE_NUMBER: 1, COPY_CODE: 1 };

let nextId = 1;

const ButtonsCard = ({ buttons, onChange }) => {
  const counts = buttons.reduce((acc, b) => {
    acc[b.type] = (acc[b.type] || 0) + 1;
    return acc;
  }, {});

  const canAdd = (type) =>
    buttons.length < 10 && (!LIMITS[type] || (counts[type] || 0) < LIMITS[type]);

  const add = (type) => {
    if (!canAdd(type)) return;
    onChange([
      ...buttons,
      {
        id: `b${nextId++}`,
        type,
        text: type === "COPY_CODE" ? "" : "",
        url: "",
        phone_number: "",
        example: "",
      },
    ]);
  };

  const update = (id, next) =>
    onChange(buttons.map((b) => (b.id === id ? { ...b, ...next } : b)));

  const remove = (id) => onChange(buttons.filter((b) => b.id !== id));

  return (
    <Card className="space-y-3">
      {buttons.length === 0 && (
        <p className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-[12px] text-slate-500">
          No buttons. Add up to 10 — for example a quick reply, a website link
          and a call button.
        </p>
      )}

      {buttons.map((button) => {
        const Icon = TYPE_META[button.type].icon;
        return (
          <div
            key={button.id}
            className="rounded-xl border border-slate-200 bg-slate-50/60 p-3"
          >
            <div className="flex items-center gap-2">
              <GripVertical size={14} className="flex-shrink-0 text-slate-300" />
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white text-slate-500 ring-1 ring-slate-200">
                <Icon size={14} />
              </span>
              <span className="flex-1 text-[12px] font-semibold text-slate-600">
                {TYPE_META[button.type].label}
              </span>
              <button
                type="button"
                onClick={() => remove(button.id)}
                className="rounded-md p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-500"
              >
                <Trash2 size={14} />
              </button>
            </div>

            <div className="mt-2.5 grid gap-2 sm:grid-cols-2">
              {button.type !== "COPY_CODE" && (
                <TextInput
                  value={button.text}
                  maxLength={25}
                  onChange={(e) => update(button.id, { text: e.target.value })}
                  placeholder="Button label"
                />
              )}

              {button.type === "URL" && (
                <TextInput
                  value={button.url}
                  onChange={(e) => update(button.id, { url: e.target.value })}
                  placeholder="https://example.com/{{1}}"
                />
              )}

              {button.type === "PHONE_NUMBER" && (
                <TextInput
                  value={button.phone_number}
                  onChange={(e) =>
                    update(button.id, { phone_number: e.target.value })
                  }
                  placeholder="+15551234567"
                />
              )}

              {button.type === "COPY_CODE" && (
                <TextInput
                  value={button.example}
                  onChange={(e) => update(button.id, { example: e.target.value })}
                  placeholder="Sample code, e.g. SAVE20"
                />
              )}

              {button.type === "URL" && countVariables(button.url) > 0 && (
                <TextInput
                  value={button.example}
                  onChange={(e) => update(button.id, { example: e.target.value })}
                  placeholder="Full sample URL for {{1}}"
                />
              )}
            </div>
          </div>
        );
      })}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <span className="text-[12px] text-slate-400">Add:</span>
        {Object.entries(TYPE_META).map(([type, { label, icon: Icon }]) => (
          <button
            key={type}
            type="button"
            disabled={!canAdd(type)}
            onClick={() => add(type)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] font-medium text-slate-600 transition hover:border-[#7cc4e8] hover:text-[#147db5] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus size={12} />
            <Icon size={12} />
            {label}
          </button>
        ))}
      </div>

      <Hint>
        Up to 10 buttons — max 2 website links and 1 call button. Quick replies
        and call-to-action buttons can be combined.
      </Hint>
    </Card>
  );
};

export default ButtonsCard;
